/**
 * StockManagement - Manage inventory/stock
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Package, Plus, Edit2, Trash2, Search, AlertTriangle,
  TrendingDown, TrendingUp, MinusCircle, PlusCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STOCK_CATEGORIES = [
  { value: 'ingredients', label: 'Ingredients' },
  { value: 'beverages', label: 'Beverages' },
  { value: 'supplies', label: 'Supplies' },
  { value: 'packaging', label: 'Packaging' },
  { value: 'cleaning', label: 'Cleaning' },
  { value: 'other', label: 'Other' }
];

const UNITS = ['pieces', 'kg', 'g', 'liters', 'ml', 'boxes', 'packs', 'bottles', 'cans', 'bags'];

const StockManagement = () => {
  const queryClient = useQueryClient();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [adjustingItem, setAdjustingItem] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch stock items
  const { data: stockItems = [], isLoading } = useQuery({
    queryKey: ['dining-stock', filterCategory, showLowStockOnly],
    queryFn: async () => {
      let url = `${API_URL}/api/admin/dining/stock`;
      const params = new URLSearchParams();
      if (filterCategory !== 'all') params.append('category', filterCategory);
      if (showLowStockOnly) params.append('low_stock_only', 'true');
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await fetch(url, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Create stock item
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/admin/dining/stock`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to create item');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dining-stock'] });
      queryClient.invalidateQueries({ queryKey: ['dining-stats'] });
      setShowAddDialog(false);
      toast.success('Stock item created');
    },
    onError: (error) => toast.error(error.message)
  });

  // Update stock item
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await fetch(`${API_URL}/api/admin/dining/stock/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to update item');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dining-stock'] });
      setEditingItem(null);
      toast.success('Stock item updated');
    },
    onError: (error) => toast.error(error.message)
  });

  // Adjust stock
  const adjustMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await fetch(`${API_URL}/api/admin/dining/stock/${id}/adjust`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to adjust stock');
      return res.json();
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['dining-stock'] });
      queryClient.invalidateQueries({ queryKey: ['dining-stats'] });
      setAdjustingItem(null);
      toast.success(`Stock adjusted: ${result.previous_quantity} → ${result.new_quantity}`);
    },
    onError: (error) => toast.error(error.message)
  });

  // Delete stock item
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/api/admin/dining/stock/${id}`, {
        method: 'DELETE',
        headers
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to delete item');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dining-stock'] });
      queryClient.invalidateQueries({ queryKey: ['dining-stats'] });
      toast.success('Stock item deleted');
    },
    onError: (error) => toast.error(error.message)
  });

  const filteredItems = stockItems.filter(item => {
    if (searchTerm) {
      return item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
             item.supplier?.toLowerCase().includes(searchTerm.toLowerCase());
    }
    return true;
  });

  const StockItemForm = ({ item, onSubmit, onCancel, isLoading }) => {
    const [formData, setFormData] = useState({
      name: item?.name || '',
      category: item?.category || 'ingredients',
      unit: item?.unit || 'pieces',
      current_quantity: item?.current_quantity || 0,
      minimum_quantity: item?.minimum_quantity || 10,
      cost_per_unit: item?.cost_per_unit || 0,
      supplier: item?.supplier || '',
      location: item?.location || '',
      expiry_date: item?.expiry_date || ''
    });

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(formData);
        }}
        className="space-y-4"
      >
        <div>
          <label className="text-sm font-medium">Item Name *</label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            placeholder="e.g., Chicken Breast"
            data-testid="input-stock-name"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Category</label>
            <Select
              value={formData.category}
              onValueChange={(value) => setFormData({ ...formData, category: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STOCK_CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium">Unit</label>
            <Select
              value={formData.unit}
              onValueChange={(value) => setFormData({ ...formData, unit: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {UNITS.map((unit) => (
                  <SelectItem key={unit} value={unit}>
                    {unit}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Current Quantity</label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={formData.current_quantity}
              onChange={(e) => setFormData({ ...formData, current_quantity: parseFloat(e.target.value) || 0 })}
            />
          </div>
          <div>
            <label className="text-sm font-medium">Minimum (Alert)</label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={formData.minimum_quantity}
              onChange={(e) => setFormData({ ...formData, minimum_quantity: parseFloat(e.target.value) || 0 })}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Cost per Unit ($)</label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={formData.cost_per_unit}
              onChange={(e) => setFormData({ ...formData, cost_per_unit: parseFloat(e.target.value) || 0 })}
            />
          </div>
          <div>
            <label className="text-sm font-medium">Storage Location</label>
            <Input
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="e.g., Freezer A"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Supplier</label>
            <Input
              value={formData.supplier}
              onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
              placeholder="Supplier name"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Expiry Date</label>
            <Input
              type="date"
              value={formData.expiry_date}
              onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" disabled={isLoading} data-testid="submit-stock-btn">
            {isLoading ? 'Saving...' : item ? 'Update Item' : 'Create Item'}
          </Button>
        </div>
      </form>
    );
  };

  // Stock Adjustment Form
  const AdjustmentForm = ({ item, onSubmit, onCancel, isLoading }) => {
    const [adjustData, setAdjustData] = useState({
      quantity: 0,
      adjustment_type: 'add',
      reason: ''
    });

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(adjustData);
        }}
        className="space-y-4"
      >
        <div className="p-4 bg-gray-50 rounded-lg">
          <p className="font-medium">{item.name}</p>
          <p className="text-sm text-gray-500">
            Current: {item.current_quantity} {item.unit}
          </p>
        </div>

        <div>
          <label className="text-sm font-medium">Adjustment Type</label>
          <Select
            value={adjustData.adjustment_type}
            onValueChange={(value) => setAdjustData({ ...adjustData, adjustment_type: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="add">
                <span className="flex items-center gap-2">
                  <PlusCircle className="h-4 w-4 text-green-500" />
                  Add Stock (Received)
                </span>
              </SelectItem>
              <SelectItem value="remove">
                <span className="flex items-center gap-2">
                  <MinusCircle className="h-4 w-4 text-blue-500" />
                  Remove (Used)
                </span>
              </SelectItem>
              <SelectItem value="waste">
                <span className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                  Waste/Expired
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-sm font-medium">Quantity ({item.unit})</label>
          <Input
            type="number"
            min="0.01"
            step="0.01"
            value={adjustData.quantity}
            onChange={(e) => setAdjustData({ ...adjustData, quantity: parseFloat(e.target.value) || 0 })}
            required
          />
        </div>

        <div>
          <label className="text-sm font-medium">Reason (optional)</label>
          <Input
            value={adjustData.reason}
            onChange={(e) => setAdjustData({ ...adjustData, reason: e.target.value })}
            placeholder="e.g., Daily delivery, Spoiled, Inventory count"
          />
        </div>

        <div className="p-3 bg-blue-50 rounded-lg">
          <p className="text-sm">
            New quantity: <strong>
              {adjustData.adjustment_type === 'add'
                ? (item.current_quantity + adjustData.quantity).toFixed(2)
                : Math.max(0, item.current_quantity - adjustData.quantity).toFixed(2)
              } {item.unit}
            </strong>
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" disabled={isLoading || adjustData.quantity <= 0}>
            {isLoading ? 'Adjusting...' : 'Confirm Adjustment'}
          </Button>
        </div>
      </form>
    );
  };

  return (
    <div className="space-y-6" data-testid="stock-management">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Stock & Inventory</h2>
          <p className="text-sm text-gray-500">Track ingredients, supplies, and inventory levels</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} data-testid="add-stock-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Item
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search stock..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {STOCK_CATEGORIES.map((cat) => (
              <SelectItem key={cat.value} value={cat.value}>
                {cat.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 cursor-pointer">
          <Switch
            checked={showLowStockOnly}
            onCheckedChange={setShowLowStockOnly}
          />
          <span className="text-sm flex items-center gap-1">
            <AlertTriangle className="h-4 w-4 text-red-500" />
            Low stock only
          </span>
        </label>
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-gray-500">Loading stock...</div>
      ) : filteredItems.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium mb-2">No stock items</h3>
            <p className="text-sm text-gray-500 mb-4">
              Add inventory items to track your stock levels
            </p>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add First Item
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredItems.map((item) => {
            const isLowStock = item.current_quantity <= item.minimum_quantity;
            
            return (
              <Card 
                key={item.id} 
                className={isLowStock ? 'border-red-200 bg-red-50' : ''}
                data-testid={`stock-item-card-${item.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-medium">{item.name}</h3>
                      <p className="text-xs text-gray-500 capitalize">{item.category}</p>
                    </div>
                    {isLowStock && (
                      <Badge variant="destructive" className="text-xs">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Low Stock
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-end justify-between mb-3">
                    <div>
                      <p className="text-3xl font-bold">{item.current_quantity}</p>
                      <p className="text-sm text-gray-500">{item.unit}</p>
                    </div>
                    <div className="text-right text-sm text-gray-500">
                      <p>Min: {item.minimum_quantity}</p>
                      {item.cost_per_unit > 0 && <p>${item.cost_per_unit}/{item.unit}</p>}
                    </div>
                  </div>

                  {/* Info */}
                  <div className="space-y-1 text-xs text-gray-400 mb-3">
                    {item.location && <p>📍 {item.location}</p>}
                    {item.supplier && <p>🏢 {item.supplier}</p>}
                    {item.expiry_date && (
                      <p className={new Date(item.expiry_date) < new Date() ? 'text-red-500' : ''}>
                        📅 Expires: {new Date(item.expiry_date).toLocaleDateString()}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-2 border-t">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={() => setAdjustingItem(item)}
                    >
                      <TrendingUp className="h-4 w-4 mr-1" />
                      Adjust
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingItem(item)}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => {
                        if (confirm('Delete this stock item?')) {
                          deleteMutation.mutate(item.id);
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Item Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Stock Item</DialogTitle>
          </DialogHeader>
          <StockItemForm
            onSubmit={(data) => createMutation.mutate(data)}
            onCancel={() => setShowAddDialog(false)}
            isLoading={createMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Item Dialog */}
      <Dialog open={!!editingItem} onOpenChange={(open) => !open && setEditingItem(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Stock Item</DialogTitle>
          </DialogHeader>
          {editingItem && (
            <StockItemForm
              item={editingItem}
              onSubmit={(data) => updateMutation.mutate({ id: editingItem.id, data })}
              onCancel={() => setEditingItem(null)}
              isLoading={updateMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Adjust Stock Dialog */}
      <Dialog open={!!adjustingItem} onOpenChange={(open) => !open && setAdjustingItem(null)}>
        <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Adjust Stock</DialogTitle>
          </DialogHeader>
          {adjustingItem && (
            <AdjustmentForm
              item={adjustingItem}
              onSubmit={(data) => adjustMutation.mutate({ id: adjustingItem.id, data })}
              onCancel={() => setAdjustingItem(null)}
              isLoading={adjustMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockManagement;
